import { DAYS_PER_YEAR, VEGA_DIVISOR } from "@/lib/constants";
import type { Greeks } from "@/types/options";

// Standard normal CDF approximation (Abramowitz & Stegun 26.2.17)
// Max error: 7.5×10⁻⁸
function normCDF(x: number): number {
  if (x >= 6) return 1;
  if (x <= -6) return 0;

  const b1 = 0.31938153;
  const b2 = -0.356563782;
  const b3 = 1.781477937;
  const b4 = -1.821255978;
  const b5 = 1.330274429;
  const pp = 0.2316419;

  const t = 1.0 / (1.0 + pp * Math.abs(x));
  const pdf = Math.exp((-x * x) / 2) / Math.sqrt(2 * Math.PI);
  const poly = ((((b5 * t + b4) * t + b3) * t + b2) * t + b1) * t;

  return x >= 0 ? 1 - pdf * poly : pdf * poly;
}

// Standard normal PDF
function normPDF(x: number): number {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

interface BSParams {
  S: number; // spot price
  K: number; // strike price
  T: number; // time to expiration in years
  r: number; // risk-free rate
  sigma: number; // implied volatility
}

function d1({ S, K, T, r, sigma }: BSParams): number {
  return (
    (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T))
  );
}

function d2(params: BSParams): number {
  return d1(params) - params.sigma * Math.sqrt(params.T);
}

export function callPrice(params: BSParams): number {
  const { S, K, T, r } = params;
  if (T <= 0) return Math.max(0, S - K);
  const _d1 = d1(params);
  const _d2 = d2(params);
  return S * normCDF(_d1) - K * Math.exp(-r * T) * normCDF(_d2);
}

export function putPrice(params: BSParams): number {
  const { S, K, T, r } = params;
  if (T <= 0) return Math.max(0, K - S);
  const _d1 = d1(params);
  const _d2 = d2(params);
  return K * Math.exp(-r * T) * normCDF(-_d2) - S * normCDF(-_d1);
}

export function callGreeks(params: BSParams): Greeks {
  const { S, K, T, r, sigma } = params;
  if (T <= 0) {
    return { delta: S > K ? 1 : 0, gamma: 0, theta: 0, vega: 0 };
  }
  const _d1 = d1(params);
  const _d2 = d2(params);
  const sqrtT = Math.sqrt(T);

  return {
    delta: normCDF(_d1),
    gamma: normPDF(_d1) / (S * sigma * sqrtT),
    theta:
      (-(S * normPDF(_d1) * sigma) / (2 * sqrtT) -
        r * K * Math.exp(-r * T) * normCDF(_d2)) /
      DAYS_PER_YEAR,
    vega: (S * normPDF(_d1) * sqrtT) / VEGA_DIVISOR,
  };
}

export function putGreeks(params: BSParams): Greeks {
  const { S, K, T, r, sigma } = params;
  if (T <= 0) {
    return { delta: S < K ? -1 : 0, gamma: 0, theta: 0, vega: 0 };
  }
  const _d1 = d1(params);
  const _d2 = d2(params);
  const sqrtT = Math.sqrt(T);

  return {
    delta: normCDF(_d1) - 1,
    gamma: normPDF(_d1) / (S * sigma * sqrtT),
    theta:
      (-(S * normPDF(_d1) * sigma) / (2 * sqrtT) +
        r * K * Math.exp(-r * T) * normCDF(-_d2)) /
      DAYS_PER_YEAR,
    vega: (S * normPDF(_d1) * sqrtT) / VEGA_DIVISOR,
  };
}

export function aggregateGreeks(greeksList: Greeks[]): Greeks {
  return greeksList.reduce(
    (acc, g) => ({
      delta: acc.delta + g.delta,
      gamma: acc.gamma + g.gamma,
      theta: acc.theta + g.theta,
      vega: acc.vega + g.vega,
    }),
    { delta: 0, gamma: 0, theta: 0, vega: 0 },
  );
}
